import { useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { WeeklyTimeseriesPoint, MonthlyTimeseriesPoint, TimeseriesPoint } from '@/hooks/useAdminStats'

interface LargeChartProps {
  title: string
  subtitle?: string
  data: WeeklyTimeseriesPoint[] | MonthlyTimeseriesPoint[] | TimeseriesPoint[]
  type: 'weekly' | 'monthly' | 'daily'
  color?: string
  height?: number
  loading?: boolean
  className?: string
  periodLabel?: string // Custom period label override
  total?: number // Total value to display prominently
  totalLabel?: string // Label for the total (e.g., "Total", "30d Total")
}

/**
 * Format date for x-axis based on chart type
 */
function formatXAxisLabel(value: string, type: 'weekly' | 'monthly' | 'daily'): string {
  const date = new Date(value)
  if (type === 'daily') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  if (type === 'weekly') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

/**
 * Format value for tooltip
 */
function formatTooltipDate(value: string, type: 'weekly' | 'monthly' | 'daily'): string {
  const date = new Date(value)
  if (type === 'daily') {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  }
  if (type === 'weekly') {
    const endDate = new Date(date)
    endDate.setDate(endDate.getDate() + 6)
    return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ 
  active, 
  payload,
  type,
}: { 
  active?: boolean
  payload?: Array<{ value: number; payload: { dateKey: string } }>
  type: 'weekly' | 'monthly' | 'daily'
}) {
  if (!active || !payload?.length) return null

  const dateKey = payload[0]?.payload?.dateKey || ''
  
  return (
    <div className="bg-background/95 backdrop-blur-sm border border-white/[0.1] rounded-lg px-3 py-2 shadow-xl">
      <p className="text-[10px] text-muted-foreground/60 mb-1">
        {formatTooltipDate(dateKey, type)}
      </p>
      <p className="text-sm font-semibold text-foreground tabular-nums">
        {payload[0].value.toLocaleString()} users
      </p>
    </div>
  )
}

/**
 * Large chart component for WAU/MAU visualization
 */
export function LargeChart({
  title,
  subtitle,
  data,
  type,
  color = '#63d706',
  height = 250,
  loading = false,
  className,
  periodLabel,
  total,
  totalLabel = 'Total',
}: LargeChartProps) {

  // Transform data for recharts
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data.map((point) => {
      let dateKey: string
      if (type === 'daily') {
        dateKey = (point as TimeseriesPoint).date
      } else if (type === 'weekly') {
        dateKey = (point as WeeklyTimeseriesPoint).weekStart
      } else {
        dateKey = (point as MonthlyTimeseriesPoint).monthStart
      }

      return {
        dateKey,
        displayDate: formatXAxisLabel(dateKey, type),
        value: point.count,
      }
    })
  }, [data, type])

  // Calculate summary stats
  const stats = useMemo(() => {
    if (chartData.length === 0) return { current: 0, avg: 0, max: 0 }
    
    const values = chartData.map(d => d.value)
    const current = values[values.length - 1] || 0
    const avg = Math.round(values.reduce((a, b) => a + b, 0) / values.length)
    const max = Math.max(...values)
    
    return { current, avg, max }
  }, [chartData])

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02] p-5',
        'transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.03]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          {subtitle && (
            <p className="text-xs text-muted-foreground/50 mt-0.5">{subtitle}</p>
          )}
        </div>
        {!loading && chartData.length > 0 && (
          <div className="flex items-center gap-4 text-xs">
            {total !== undefined && (
              <div className="text-right">
                <span className="text-muted-foreground/50 block">{totalLabel}</span>
                <span className="text-foreground font-semibold tabular-nums text-lg">
                  {total.toLocaleString()}
                </span>
              </div>
            )}
            {total === undefined && (
              <>
                <div className="text-right">
                  <span className="text-muted-foreground/50 block">Current</span>
                  <span className="text-foreground font-medium tabular-nums">
                    {stats.current.toLocaleString()}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-muted-foreground/50 block">Average</span>
                  <span className="text-foreground font-medium tabular-nums">
                    {stats.avg.toLocaleString()}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Chart */}
      {loading ? (
        <div 
          className="flex items-center justify-center bg-white/[0.02] rounded"
          style={{ height }}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/40" />
        </div>
      ) : chartData.length === 0 ? (
        <div 
          className="flex items-center justify-center text-muted-foreground/30 text-sm"
          style={{ height }}
        >
          No data available
        </div>
      ) : (
        <div style={{ height, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 8, right: 8, left: 0, bottom: 24 }}
            >
              <defs>
                <linearGradient id={`gradient-large-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="50%" stopColor={color} stopOpacity={0.12} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="rgba(255,255,255,0.04)" 
                vertical={false}
              />
              
              <XAxis 
                dataKey="displayDate" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={type === 'daily' ? 50 : type === 'weekly' ? 60 : 40}
              />
              
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                width={36}
                tickFormatter={(value) => {
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`
                  return value.toString()
                }}
              />
              
              <Tooltip 
                content={<CustomTooltip type={type} />}
                cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
              />
              
              {/* Main area */}
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#gradient-large-${color.replace('#', '')})`}
                animationDuration={800}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Period indicator - hidden if periodLabel is whitespace-only */}
      {(periodLabel === undefined || periodLabel.trim() !== '') && (
        <div className="mt-3 text-[10px] text-muted-foreground/40">
          {periodLabel || (type === 'daily' ? 'Past 30 days' : type === 'weekly' ? 'Past 12 weeks' : 'Past 6 months')}
        </div>
      )}
    </div>
  )
}
